import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Trophy } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, inputCls } from "./components";

const EMPTY = { category: "Program Record", title: "", holder: "", year: "", description: "" };
const CATEGORIES = ["Program Record", "Team Achievement", "Championship", "Award", "Milestone"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 60 }, (_, i) => CURRENT_YEAR - i);

export default function RecordsSection() {
  const { items, loading, reload } = useResource("records");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));

  const save = async () => {
    if (!editing.title.trim()) {
      toast.error("Please enter a title for the record or achievement.");
      return;
    }
    setSaving(true);
    const payload = {
      category: editing.category,
      title: editing.title.trim(),
      holder: (editing.holder || "").trim(),
      year: Number(editing.year) || null,
      description: (editing.description || "").trim(),
    };
    try {
      if (editing.id) await api.put(`/admin/records/${editing.id}`, payload);
      else await api.post("/admin/records", payload);
      toast.success("Saved! The Program Records page is updated.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save. Please try again.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/records/${deleting.id}`);
      toast.success("Entry removed.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the entry.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-records-section">
      <SectionHeader
        title="Program Records & Achievements"
        subtitle="Document championships, program records, awards, and milestones. Only enter verified information — it appears on the public page right away."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-record-button">
            <Plus size={17} /> Add New Entry
          </PrimaryBtn>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="records-list">
        {sorted.length === 0 && (
          <p className="col-span-full bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
            No records or achievements yet. Click "Add New Entry" to add the first one.
          </p>
        )}
        {sorted.map((r) => (
          <div key={r.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid={`records-row-${r.id}`}>
            <div className="h-12 w-12 rounded-md bg-[#0F4C2A] flex items-center justify-center shrink-0">
              <Trophy size={20} className="text-[#F59E0B]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-condensed text-lg font-bold uppercase text-slate-900 truncate">{r.title}</p>
              <p className="text-xs text-[#D97706] font-bold uppercase tracking-wide truncate">{r.category}</p>
              <p className="text-xs text-slate-500 font-semibold">{[r.holder, r.year].filter(Boolean).join(" · ")}</p>
            </div>
            <button onClick={() => setEditing({ ...r })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit entry" data-testid={`records-edit-${r.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => setDeleting(r)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete entry" data-testid={`records-delete-${r.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Entry" : "Add Record or Achievement"} onClose={() => setEditing(null)} testid="records-modal">
          <div className="space-y-4">
            <Field label="Category">
              <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="records-form-category">
                {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
              </select>
            </Field>
            <Field label="Title">
              <input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Single-Season Wins Record" data-testid="records-form-title" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Player / Team (optional)" hint="Who holds this record or earned this honor">
                <input className={inputCls} value={editing.holder} onChange={(e) => setEditing({ ...editing, holder: e.target.value })} data-testid="records-form-holder" />
              </Field>
              <Field label="Year (optional)">
                <select className={inputCls} value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} data-testid="records-form-year">
                  <option value="">Choose...</option>
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Details (optional)">
              <textarea rows={3} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="records-form-description" />
            </Field>
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="records-form-save">
                {saving ? "Saving..." : "Save Entry"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="records-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Remove "${deleting.title}"? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="records-delete-confirm"
        />
      )}
    </div>
  );
}
